/* eslint-disable @typescript-eslint/member-ordering */
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthError,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  signIn,
  SignInOutput,
  signOut,
} from '@aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';
import { from, map, Observable } from 'rxjs';

import { FeatureFlagsService } from '@/core/services/feature-flags.service';
import { ThemeService } from '@/core/theme';

import { AppRoute, canAccessRoute, getFirstAllowedRoute } from './constants/group-access.constants';
import { AmplifySession, AmplifyUser, CognitoIdToken, UserData } from './interfaces/token-data.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly featureFlags = inject(FeatureFlagsService);

  // Reactive authentication state using signals
  private readonly isAuthenticatedSignal = signal<boolean>(false);
  private readonly userDataSignal = signal<UserData | null>(null);
  private readonly accessTokenSignal = signal<string | null>(null);

  // Computed properties
  readonly isAuthenticated = computed(() => this.isAuthenticatedSignal());
  readonly userData = computed(() => this.userDataSignal());
  readonly accessToken = computed(() => this.accessTokenSignal());

  // In-memory redirect URL storage
  private redirectUrl: string | null = null;

  // Flag to track if user has been explicitly logged out
  private isExplicitlyLoggedOut = false;

  /**
   * Set secure cookie with JWT token for video authentication
   */
  private setSecureCookie(token: string): void {
    const expires = new Date();
    expires.setHours(expires.getHours() + 24);
    document.cookie =
      `access_token=${encodeURIComponent(token)}; ` +
      `expires=${expires.toUTCString()}; ` +
      `path=/; ` +
      `secure; ` +
      `samesite=strict`;
  }

  /**
   * Clear secure cookie
   */
  private clearSecureCookie(): void {
    document.cookie = `access_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }

  /**
   * Decode the Cognito idToken and extract the `cognito:groups` claim.
   * Returns an empty array if the token is missing or malformed so auth never breaks.
   */
  private getGroupsFromSession(session: AmplifySession): string[] {
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) return [];

    try {
      const decoded = jwtDecode<CognitoIdToken>(idToken);
      const groups = decoded['cognito:groups'] ?? [];
      return groups;
    } catch {
      return [];
    }
  }

  /**
   * Build the UserData object from the Amplify user and session.
   * Single source of truth so the signal path and guard path stay identical.
   */
  private buildUserData(user: AmplifyUser, session: AmplifySession): UserData {
    return {
      roles: user.signInDetails?.loginId ? [user.signInDetails.loginId] : [],
      groups: this.getGroupsFromSession(session),
      email: user.signInDetails?.loginId || user.username || '',
      emailVerified: true,
      sub: user.userId || '',
      username: user.username || user.signInDetails?.loginId || '',
    };
  }

  /**
   * Update reactive state based on Amplify authentication
   */
  private updateAuthenticationState(user: AmplifyUser, session: AmplifySession): void {
    const accessToken = session.tokens?.accessToken?.toString() || null;

    const userData: UserData = this.buildUserData(user, session);

    // Update signals
    this.isAuthenticatedSignal.set(true);
    this.userDataSignal.set(userData);
    this.accessTokenSignal.set(accessToken);

    // Set secure cookie for video authentication
    if (accessToken) {
      this.setSecureCookie(accessToken);
    }
  }

  /**
   * Clear authentication state
   */
  private clearAuthenticationState(): void {
    this.isAuthenticatedSignal.set(false);
    this.userDataSignal.set(null);
    this.accessTokenSignal.set(null);

    // Clear secure cookie
    this.clearSecureCookie();
  }

  /**
   * Check authentication status using Amplify's official API
   */
  async checkAuthenticationStatus(): Promise<boolean> {
    try {
      // If user was explicitly logged out, don't check Amplify until next login
      if (this.isExplicitlyLoggedOut) {
        this.clearAuthenticationState();
        return false;
      }

      const user = (await getCurrentUser()) as AmplifyUser;
      const session = (await fetchAuthSession()) as AmplifySession;

      if (session.tokens?.accessToken && user) {
        this.updateAuthenticationState(user, session);
        return true;
      }

      this.clearAuthenticationState();
      return false;
    } catch (error: unknown) {
      // Handle UserUnAuthenticatedException gracefully - this is expected for unauthenticated users
      if ((error as AuthError).name === 'UserUnAuthenticatedException') {
        this.clearAuthenticationState();
        return false;
      }

      // Log other authentication errors
      console.error('Authentication check failed:', error);
      this.clearAuthenticationState();
      return false;
    }
  }

  /**
   * Get current user data using Amplify API
   */
  getUserData(): Observable<UserData | null> {
    return from(this.getCurrentUserData());
  }

  /**
   * Get current user data directly from Amplify
   */
  async getCurrentUserData(): Promise<UserData | null> {
    try {
      const user = (await getCurrentUser()) as AmplifyUser;
      const session = (await fetchAuthSession()) as AmplifySession;
      return this.buildUserData(user, session);
    } catch (error: unknown) {
      // Handle UserUnAuthenticatedException gracefully - this is expected for unauthenticated users
      if ((error as AuthError).name === 'UserUnAuthenticatedException') {
        return null;
      }

      console.error('Failed to get user data:', error);
      return null;
    }
  }

  /**
   * Get current access token using Amplify's fetchAuthSession
   */
  async getCurrentAccessToken(): Promise<string | null> {
    try {
      const session = (await fetchAuthSession()) as AmplifySession;
      return session.tokens?.accessToken?.toString() || null;
    } catch (error: unknown) {
      // Handle UserUnAuthenticatedException gracefully - this is expected for unauthenticated users
      if ((error as AuthError).name === 'UserUnAuthenticatedException') {
        return null;
      }

      console.error('Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Get current access token as Observable
   */
  getAccessToken(): Observable<string | null> {
    return from(this.getCurrentAccessToken());
  }

  /**
   * Logout user using Amplify's signOut
   */
  async logout(): Promise<void> {
    try {
      // Set explicit logout flag to prevent re-authentication
      this.isExplicitlyLoggedOut = true;

      // IMMEDIATELY clear reactive state
      this.clearAuthenticationState();

      // IMMEDIATELY clear any in-memory data
      this.redirectUrl = null;

      // IMMEDIATELY navigate to login to prevent flash
      this.router.navigate(['/login']);

      // Perform Amplify logout and wait for completion
      try {
        await signOut({ global: false });
        console.warn('Amplify signOut completed successfully');
      } catch (signOutError) {
        console.error('Amplify signOut failed:', signOutError);
        // Don't throw here - we've already cleared our state
      }

      console.warn('User logged out successfully');
    } catch (error) {
      console.error('Error during logout:', error);
      // Ensure we always redirect to login
      this.router.navigate(['/login']);
    }
  }

  /**
   * Check if user is already authenticated using Amplify API
   */
  async checkExistingAuthentication(): Promise<boolean> {
    return await this.checkAuthenticationStatus();
  }

  /**
   * Initiate login process with default authentication flow
   */
  async login(username: string, password: string): Promise<SignInOutput> {
    try {
      const signInOutput = await signIn({
        username,
        password,
      });

      if (signInOutput.isSignedIn) {
        // Reset logout flag on successful login
        this.isExplicitlyLoggedOut = false;
        await this.checkAuthenticationStatus();
      }
      return signInOutput;
    } catch (error: unknown) {
      console.error('Login failed:', error);

      // Handle UserAlreadyAuthenticatedException specifically
      if ((error as AuthError).name === 'UserAlreadyAuthenticatedException') {
        console.warn('User already authenticated via Amplify, checking existing session...');
        try {
          const isAuthenticated = await this.checkAuthenticationStatus();
          if (isAuthenticated) {
            // Throw a custom error that the component can handle
            throw new Error('USER_ALREADY_AUTHENTICATED');
          }
        } catch (processError) {
          console.error('Failed to check existing authentication:', processError);
          // Clear state and let user try again
          this.clearAuthenticationState();
        }
      }

      throw error as AuthError;
    }
  }

  /**
   * Confirm sign in with new password (for first-time users)
   */
  async confirmSignInWithNewPassword(newPassword: string): Promise<SignInOutput> {
    try {
      const signInOutput = await confirmSignIn({
        challengeResponse: newPassword,
      });

      if (signInOutput.isSignedIn) {
        // Reset logout flag on successful login
        this.isExplicitlyLoggedOut = false;
        await this.checkAuthenticationStatus();
      }

      return signInOutput;
    } catch (error: unknown) {
      console.error('New password confirmation failed:', error);
      throw error as AuthError;
    }
  }

  /**
   * Initiate password reset process
   */
  async resetPassword(username: string): Promise<void> {
    try {
      await resetPassword({ username });
      console.warn('Password reset email sent successfully');
    } catch (error: unknown) {
      console.error('Password reset failed:', error);
      throw error as AuthError;
    }
  }

  /**
   * Confirm password reset with verification code
   */
  async confirmPasswordReset(username: string, confirmationCode: string, newPassword: string): Promise<void> {
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
      console.warn('Password reset confirmed successfully');
    } catch (error: unknown) {
      console.error('Password reset confirmation failed:', error);
      throw error as AuthError;
    }
  }

  /**
   * Initiate OAuth login process (for future implementation)
   */
  async loginWithOAuth(): Promise<void> {
    try {
      console.warn('OAuth login not yet implemented');
      throw new Error('OAuth login not yet implemented');
    } catch (error) {
      console.error('OAuth login failed:', error);
      throw error;
    }
  }

  /**
   * Cognito groups of the current user, derived from the decoded idToken.
   */
  readonly groups = computed(() => this.userDataSignal()?.groups ?? []);

  /**
   * Check if the current user belongs to a specific Cognito group (synchronous).
   */
  hasGroup(group: string): boolean {
    return this.groups().includes(group);
  }

  /**
   * Check if the current user's groups grant access to a given route (synchronous).
   * Also enforces any team-level exclusion regardless of group (see group-access.constants.ts).
   */
  canAccess(route: AppRoute): boolean {
    return canAccessRoute(this.groups(), route, this.themeService.currentTeamId());
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): Observable<boolean> {
    return this.getUserData().pipe(
      map((userData: UserData | null) => {
        if (!userData || !userData.roles) return false;
        return userData.roles.includes(role);
      })
    );
  }

  /**
   * Store redirect URL for post-login navigation
   */
  storeRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  /**
   * First section (as an AppRoute) the current user's groups grant access to,
   * after team-level exclusions and disabled feature flags — null when none apply.
   */
  firstAllowedRoute(): AppRoute | null {
    return getFirstAllowedRoute(this.groups(), this.themeService.currentTeamId(), this.featureFlags.flags);
  }

  /**
   * Default landing route for the current user: the first section their
   * Cognito groups grant access to (falls back to /pre-game).
   */
  getDefaultRoute(): string {
    const route = this.firstAllowedRoute();
    return route ? `/${route}` : '/pre-game';
  }

  /**
   * Get and clear stored redirect URL. When nothing was stored, send the user
   * to their first allowed section instead of a hardcoded default.
   */
  getAndClearRedirectUrl(): string {
    const url = this.redirectUrl || this.getDefaultRoute();
    this.redirectUrl = null;
    return url;
  }

  /**
   * Check if user is currently authenticated using Amplify
   */
  async isTokenValid(): Promise<boolean> {
    try {
      await getCurrentUser();
      const session = await fetchAuthSession();
      return !!session.tokens?.accessToken;
    } catch (error: unknown) {
      // Handle UserUnAuthenticatedException gracefully - this is expected for unauthenticated users
      if ((error as AuthError).name === 'UserUnAuthenticatedException') {
        return false;
      }

      // Log other errors for debugging
      console.error('Token validation failed:', error);
      return false;
    }
  }

  /**
   * Get token expiry information from current session
   */
  async getTokenExpiry(): Promise<Date | null> {
    try {
      const session = (await fetchAuthSession()) as AmplifySession;
      const accessToken = session.tokens?.accessToken;
      if (accessToken) {
        // Parse JWT to get expiry
        const payload = JSON.parse(atob(accessToken.toString().split('.')[1]));
        return payload.exp ? new Date(payload.exp * 1000) : null;
      }
      return null;
    } catch (error: unknown) {
      // Handle UserUnAuthenticatedException gracefully - this is expected for unauthenticated users
      if ((error as AuthError).name === 'UserUnAuthenticatedException') {
        return null;
      }

      console.error('Failed to get token expiry:', error);
      return null;
    }
  }

  /**
   * Initialize authentication state on app startup
   */
  initializeAuth(): Observable<boolean> {
    return from(this.checkAuthenticationStatus());
  }
}
